import _ from 'underscore';
import { assign } from 'xstate';

export const FORM_STATES = {
  FETCHING: 'FETCHING',
  INIT: 'INIT',
  LOADING: 'LOADING',
  LOADING_FAILED: 'LOADING_FAILED',
  ACTIVE: 'ACTIVE',
  CANCELING: 'CANCELING',
  CONFIRMING: 'CONFIRMING',
  SAVING: 'SAVING',
  SAVING_SUCCEEDED: 'SAVING_SUCCEEDED',
  SAVING_FAILED: 'SAVING_FAILED',
  CANCELED: 'CANCELED',
};

export const FORM_EVENTS = {
  UPDATE_CONFIG: 'UPDATE_CONFIG',
  DONE_FETCHING: 'DONE_FETCHING',

  LOAD: 'LOAD',
  DONE_LOADING: 'DONE_LOADING',
  FAILED_LOADING: 'FAILED_LOADING',

  CHANGE_DATA: 'CHANGE_DATA',

  CANCEL: 'CANCEL',
  ABORT_CANCEL: 'ABORT_CANCEL',
  CONFIRM_CANCEL: 'CONFIRM_CANCEL',

  SAVE: 'SAVE',
  ABORT_SAVE: 'ABORT_SAVE',
  CONFIRM_SAVE: 'CONFIRM_SAVE',

  DONE_SAVING: 'DONE_SAVING',
  FAILED_SAVING: 'FAILED_SAVING',
};

export const DefaultActions = {
  setInitialData: assign({
    data: (context, event) => (!_.isUndefined(event.data) ? _.clone(event.data) : context.data),
    initialData: (context, event) => (!_.isUndefined(event.data) ? _.clone(event.data) : context.initialData),
  }),
  setError: assign({
    error: (context, event) => event.error,
  }),
  changeData: assign({
    data: (context, event) => {
      if (_.isFunction(event.data)) {
        const newData = event.data(context.data);
        if (_.isEqual(newData, context.data)) {
          return context.data;
        }
        return _.clone(newData);
      }
      if (_.isUndefined(event.data) || _.isEqual(event.data, context.data)) {
        return context.data;
      }
      return _.clone(event.data);
    },
  }),
  cleanErrorIfDataChanged: assign({
    error: (context, event) => {
      if (_.isFunction(event.data)) {
        const newData = event.data(context.data);
        if (_.isEqual(newData, context.data)) {
          return context.error;
        }
        return undefined;
      }
      if (_.isUndefined(event.data) || _.isEqual(event.data, context.data)) {
        return context.error;
      }
      return undefined;
    },
  }),
  setDataOnError: assign({
    data: (context, event) => (!_.isUndefined(event.data) ? _.clone(event.data) : context.data),
    // allow change initialData for partial successful load/save
    initialData: (context, event) => (!_.isUndefined(event.initialData) ? _.clone(event.initialData) : context.initialData),
  }),
  setSaveFailed: assign({
    saveFailed: () => true,
  }),
  setIsValid: assign({
    isValid: (context, event) => (_.isBoolean(event.isValid) ? event.isValid : context.isValid),
  }),
  setIsDirty: assign({
    isDirty: (context, event) => !_.isEqual(context.initialData, event.data),
  }),
  cleanError: assign({
    error: () => undefined,
  }),
  resetStates: assign({
    isValid: () => true,
    isDirty: () => false,
  }),
  updateConfig: assign({
    config: (context, event) => _.extend({}, context.config, event.config),
  }),

  // Default callback actions on state changes
  onFetching: _.noop,
  onLoading: _.noop,
  onDoneLoading: _.noop,
  onFailedLoading: _.noop,
  onActive: _.noop,
  onCanceling: _.noop,
  onConfirming: _.noop,
  onSaving: _.noop,
  onDoneSaving: _.noop,
  onFailedSaving: _.noop,
  onCanceled: _.noop,
  onSyncContext: _.noop, // This should be called when the context is updated
};

export const DefaultGuards = {
  // Can be overridden thru machineOptions.guards
  // Eg. needsSaving: context => context.isDirty,
  isManualLoad: () => false,
  needsLoading: () => true,
  needsSaving: () => true,
  needsConfirming: () => false,
  needsCanceling: () => false,
};

export const DefaultMachineId = 'FormStateMachine';

export const DefaultInitialState = FORM_STATES.LOADING;

export const DefaultContext = {
  config: {},
  data: undefined,
  initialData: undefined,
  error: undefined,
  isValid: true,
  isDirty: false,
};

export const FormMachine = {
  id: DefaultMachineId,
  initial: DefaultInitialState,
  context: DefaultContext,
  preserveActionOrder: true,
  states: {
    [FORM_STATES.FETCHING]: {
      entry: ['onFetching'],
      on: {
        [FORM_EVENTS.UPDATE_CONFIG]: {
          actions: ['updateConfig', 'onSyncContext'],
        },
        [FORM_EVENTS.DONE_FETCHING]: [
          { target: FORM_STATES.INIT, cond: 'isManualLoad' },
          { target: FORM_STATES.LOADING, cond: 'needsLoading' },
          { target: FORM_STATES.ACTIVE },
        ],
      },
    },
    [FORM_STATES.INIT]: {
      on: {
        [FORM_EVENTS.LOAD]: [
          { target: FORM_STATES.LOADING, cond: 'needsLoading' },
          { target: FORM_STATES.ACTIVE },
        ],
      },
    },
    [FORM_STATES.LOADING]: {
      entry: ['onLoading'],
      on: {
        [FORM_EVENTS.DONE_LOADING]: {
          target: FORM_STATES.ACTIVE,
          actions: ['setInitialData', 'onDoneLoading', 'onSyncContext'],
        },
        [FORM_EVENTS.FAILED_LOADING]: {
          target: FORM_STATES.LOADING_FAILED,
          actions: ['setDataOnError', 'setError', 'onSyncContext'],
        },
      },
    },
    [FORM_STATES.LOADING_FAILED]: {
      entry: ['onFailedLoading'],
      on: {
        [FORM_EVENTS.LOAD]: FORM_STATES.LOADING,
      },
    },
    [FORM_STATES.ACTIVE]: {
      entry: ['onActive', 'onSyncContext'],
      on: {
        [FORM_EVENTS.CHANGE_DATA]: {
          actions: ['cleanErrorIfDataChanged', 'changeData', 'setIsValid', 'setIsDirty', 'onSyncContext'],
        },
        [FORM_EVENTS.CANCEL]: [
          { target: FORM_STATES.CANCELING, cond: 'needsCanceling' },
          { target: FORM_STATES.CANCELED },
        ],
        [FORM_EVENTS.SAVE]: [
          { target: FORM_STATES.CONFIRMING, cond: 'needsConfirming' },
          { target: FORM_STATES.SAVING, cond: 'needsSaving' },
          { target: FORM_STATES.SAVING_SUCCEEDED },
        ],
        [FORM_EVENTS.LOAD]: [
          { target: FORM_STATES.LOADING, cond: 'needsLoading' },
          { target: FORM_STATES.ACTIVE },
        ],
      },
    },
    [FORM_STATES.CANCELING]: {
      entry: ['onCanceling'],
      on: {
        [FORM_EVENTS.CONFIRM_CANCEL]: FORM_STATES.CANCELED,
        [FORM_EVENTS.ABORT_CANCEL]: FORM_STATES.ACTIVE,
      },
    },
    [FORM_STATES.CONFIRMING]: {
      entry: ['onConfirming'],
      on: {
        [FORM_EVENTS.CHANGE_DATA]: {
          actions: ['cleanErrorIfDataChanged', 'changeData', 'setIsValid', 'setIsDirty', 'onSyncContext'],
        },
        [FORM_EVENTS.CONFIRM_SAVE]: [
          { target: FORM_STATES.SAVING, cond: 'needsSaving' },
          { target: FORM_STATES.SAVING_SUCCEEDED },
        ],
        [FORM_EVENTS.ABORT_SAVE]: FORM_STATES.ACTIVE,
      },
    },
    [FORM_STATES.SAVING]: {
      entry: ['onSaving'],
      on: {
        [FORM_EVENTS.CHANGE_DATA]: {
          actions: ['cleanErrorIfDataChanged', 'changeData', 'setIsValid', 'setIsDirty', 'onSyncContext'],
        },
        [FORM_EVENTS.DONE_SAVING]: {
          target: FORM_STATES.SAVING_SUCCEEDED,
          actions: ['setInitialData', 'resetStates', 'cleanError', 'onSyncContext'],
        },
        [FORM_EVENTS.FAILED_SAVING]: {
          target: FORM_STATES.SAVING_FAILED,
          actions: ['setSaveFailed', 'setDataOnError', 'setError', 'onSyncContext'],
        },
        [FORM_EVENTS.ABORT_SAVE]: FORM_STATES.ACTIVE,
      },
    },
    [FORM_STATES.SAVING_SUCCEEDED]: {
      entry: ['onDoneSaving'],
      after: {
        0: { target: FORM_STATES.ACTIVE },
      },
    },
    [FORM_STATES.SAVING_FAILED]: {
      entry: ['onFailedSaving'],
      after: {
        0: { target: FORM_STATES.ACTIVE },
      },
    },
    [FORM_STATES.CANCELED]: {
      entry: ['onCanceled'],
      after: {
        0: { target: FORM_STATES.ACTIVE },
      },
    },
  },
};
